export class DomUtils {

  public constructor(private _window: Window) {}

  public get window() {
    return this._window
  }

  public hasParentNode(startNode: Element, parentToFind: Element) {
    if (!(parentToFind instanceof Element) || !(startNode instanceof Element)) {
      return false
    }
    return startNode.parentElement === parentToFind || this.hasParentNode(startNode.parentElement, parentToFind)
  }

  public findParentBy(domElement: HTMLElement, searchCriteria: {id?: string, tagName?: string }): HTMLElement {
    if (!domElement || !domElement.parentElement || typeof searchCriteria !== 'object') {
      return undefined
    }

    const propertiesToSearch = Object.keys(searchCriteria)
    const isMatchingElement = propertiesToSearch.every((propertyName) => {
      const lcSearchPropertyValue = searchCriteria[propertyName].toString().toLowerCase()
      const lcCurrentElementPropertyValue = domElement[propertyName].toString().toLowerCase()
      return lcSearchPropertyValue === lcCurrentElementPropertyValue
    })

    if (isMatchingElement) {
      return domElement
    }
    return this.findParentBy(domElement.parentElement, searchCriteria)
  }

  public getScrollPosition(): {x: number, y: number} {
    let xPos = 0
    let yPos = 0

    if (typeof this._window.pageYOffset !== 'undefined') {
      xPos = this._window.pageXOffset
      yPos = this._window.pageYOffset
    } else {
      const html = this._window.document.documentElement
      const body = this._window.document.body
      xPos = (html.scrollLeft || body.scrollLeft || 0)
      yPos = (html.scrollTop || body.scrollTop || 0)
    }
    return {
      x: xPos,
      y: yPos
    }
  }

  public createElement(tagName: string) {
    return this._window.document.createElement(tagName)
  }

  public getElement(elementOrSelector: Element | string) {
    if (typeof elementOrSelector === 'string') {
      elementOrSelector = this._window.document.querySelector(elementOrSelector)
    }
    return elementOrSelector
  }

  public setMinHeight(domElement: HTMLElement, cssHeightValue: number|string): void {
    let newMinHeight = cssHeightValue as string
    if (typeof newMinHeight === 'number') {
      newMinHeight = `${cssHeightValue}px`
    }
    domElement.style.minHeight = newMinHeight
  }

  public getWindowHeight(): number {
    return 'innerHeight' in this._window ? this._window.innerHeight : this._window.document.documentElement.offsetHeight
  }

  public getElementHeight(el: HTMLElement) {
    return el.offsetHeight
  }

  public getElementPosition(el: HTMLElement): {x: number, y: number} {
    const rect = el.getBoundingClientRect()
    const scrollPosition = this.getScrollPosition()
    return {
      x: rect.left + scrollPosition.x,
      y: rect.top + scrollPosition.y
    }
  }

  public getElementPositionWithMargin(el: HTMLElement): {x: number, y: number} {
    const elMargin = parseInt(this._window.getComputedStyle(el).marginTop, 10)
    const position = this.getElementPosition(el)
    position.y -= elMargin
    return position
  }

  public getElementPositionInViewport(el: HTMLElement): {x: number, y: number} {
    let xPos = 0
    let yPos = 0

    while (el) {
      if (el.tagName.toLowerCase() === 'body') {
        // deal with browser quirks with body/window/document and page scroll
        const xScroll = el.scrollLeft || this._window.document.documentElement.scrollLeft
        const yScroll = el.scrollTop || this._window.document.documentElement.scrollTop

        xPos += (el.offsetLeft - xScroll + el.clientLeft)
        yPos += (el.offsetTop - yScroll + el.clientTop)
      } else {
        // for all other non-BODY elements
        xPos += (el.offsetLeft - el.scrollLeft + el.clientLeft)
        yPos += (el.offsetTop - el.scrollTop + el.clientTop)
      }

      el = el.offsetParent as HTMLElement
    }
    return {
      x: xPos,
      y: yPos
    }
  }

  public getBodyBackgroundColor(): string {
    return this._window.document.body.style['background-color']
  }

  public setBodyBackgroundColor(color: string): void {
    this._window.document.body.style['background-color'] = color
  }

  public scrollToTop() {
    this.window.scrollTo(window.scrollX, 0)
  }

  public getBodyBackgroundImage(): string {
    return this.window.document.body.style['background-image']
  }

  public setBodyBackgroundImage(image: string): void {
    this.window.document.body.style['background-image'] = image
  }

  public removeBodyBackgroundImage(): void {
    this.window.document.body.style['background-image'] = 'none'
  }
}



// WEBPACK FOOTER //
// ./node_modules/@mprep/shared/lib/helpers/dom-utils/dom-utils.ts